import {
  hardwareReadingValue,
  serialKindMismatchWarning,
  type HardwareCheck,
  type InverterIsolatorOffer,
} from '@cer-autofill/shared';
import {
  PHOTO_READING_CONFLICT,
  acceptOnlySuggestionNeedsReview,
  displayHardwareValue,
  hardwareReadingTicks,
  type HardwareReadingTick,
} from '../lib/auto-hardware-checks.js';

export function SuggestionReviewMark() {
  return <span className="suggestion-review-badge">Review</span>;
}

function fallbackNote(tick: HardwareReadingTick): string | null {
  if (!tick.fallback || !tick.reading) return null;
  if (tick.field === 'acCurrentNominalA')
    return 'Maximum AC current fallback — confirm before applying.';
  if (tick.field === 'acPowerRatingW')
    return 'Rated apparent power (VA) fallback — confirm watts before applying.';
  if (tick.field === 'dcCurrentMaximumA') {
    const currents = tick.reading.inputCurrentsA?.join(' + ');
    return `Sum of labelled PV input currents (${currents} A) — confirm before applying.`;
  }
  return null;
}

function readingValue(field: string, value: string): unknown {
  try {
    return hardwareReadingValue(field, value);
  } catch {
    return value;
  }
}

/** One check's comparison table. Checkboxes follow the pre-tick unless the reviewer changes them. */
export function HardwareCheckFields({
  result,
  row,
  siblings = [],
  overrides,
  onToggle,
  disabled = false,
  photoPromptKey,
}: {
  result: HardwareCheck;
  row: Record<string, unknown> | undefined;
  siblings?: readonly HardwareCheck[];
  overrides: Readonly<Record<string, boolean>>;
  onToggle: (field: string, checked: boolean) => void;
  disabled?: boolean;
  /** Prompt the checked photo is filed under — used for serial kind mismatch warnings. */
  photoPromptKey?: string | undefined;
}) {
  const ticks = hardwareReadingTicks(result, row, siblings);
  return (
    <div className="hardware-photo-check__table">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Current</th>
            <th>Photo reading</th>
            <th>Apply</th>
          </tr>
        </thead>
        <tbody>
          {ticks.map((tick) => {
            const stored = row?.[tick.field];
            const currentValue =
              tick.field === 'serials' && Array.isArray(stored) ? stored.slice(0, 1) : stored;
            const note = fallbackNote(tick);
            const checked = overrides[tick.field] ?? tick.preTicked;
            const needsReview = acceptOnlySuggestionNeedsReview(tick, checked);
            return (
              <tr
                key={tick.field}
                className={
                  needsReview
                    ? 'photo-check-review'
                    : tick.reading
                      ? tick.matches
                        ? 'photo-check-match'
                        : 'photo-check-difference'
                      : ''
                }
              >
                <th scope="row">
                  {tick.label}
                  {needsReview && <SuggestionReviewMark />}
                </th>
                <td data-label="Current">{displayHardwareValue(currentValue)}</td>
                <td data-label="Photo reading">
                  {note && <strong>{note} </strong>}
                  {tick.reading ? (
                    <>
                      <strong>
                        {displayHardwareValue(readingValue(tick.field, tick.reading.value))}
                      </strong>
                      <small>
                        {tick.matches
                          ? 'Matches'
                          : row?.[tick.field] === undefined || row[tick.field] === ''
                            ? 'Missing in report'
                            : 'Differs'}{' '}
                        · “{tick.reading.printedText}”
                        {tick.conflict ? ` ${PHOTO_READING_CONFLICT}` : ''}
                      </small>
                      {(() => {
                        const mismatch = serialKindMismatchWarning(
                          result.kind,
                          tick.reading,
                          photoPromptKey,
                        );
                        return mismatch ? (
                          <p role="status" className="hardware-serial-kind-warning">
                            {mismatch}
                          </p>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <span className={tick.uncertain ? 'photo-check-uncertain' : 'muted'}>
                      {tick.uncertain
                        ? 'Needs confirmation — recheck a closer crop or enter manually.'
                        : 'Not read'}
                    </span>
                  )}
                </td>
                <td data-label="Apply">
                  {tick.keepsExisting ? (
                    'Existing value kept'
                  ) : tick.reading && !tick.matches ? (
                    <input
                      aria-label={`Apply ${tick.label}`}
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(event) => onToggle(tick.field, event.target.checked)}
                    />
                  ) : tick.matches ? (
                    '✓'
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type IsolatorOfferView = {
  index: number | undefined;
  showPicker: boolean;
  choices: number[];
  offers: InverterIsolatorOffer[];
};

function offerReading(offer: InverterIsolatorOffer): string {
  if (offer.needsConfirmation)
    return 'Needs confirmation — recheck a closer crop or enter manually.';
  if (!offer.reading) return 'Not read';
  const value = displayHardwareValue(readingValue(offer.sourceField, offer.reading.value));
  const note =
    offer.blocked === 'existing'
      ? 'Existing value kept'
      : offer.blocked === 'isolator-label'
        ? 'DC isolator label is used'
        : '';
  return `${note ? `${note}. ` : ''}${value} · “${offer.reading.printedText}”${
    offer.conflict ? ` ${PHOTO_READING_CONFLICT}` : ''
  }`;
}

/** Inverter spec-label ratings offered for one Integrated or blank DC isolator. */
export function InverterIsolatorOffers({
  view,
  overrides,
  disabled = false,
  onIsolatorIndex,
  onToggle,
}: {
  view: IsolatorOfferView;
  overrides: Readonly<Record<string, boolean>>;
  disabled?: boolean;
  onIsolatorIndex: (index: number | undefined) => void;
  onToggle: (field: string, checked: boolean) => void;
}) {
  if (!view.showPicker && view.offers.length === 0) return null;
  return (
    <div className="hardware-photo-check__table">
      {view.showPicker && view.choices.length > 0 && (
        <label>
          DC isolator for inverter ratings
          <select
            value={view.index ?? ''}
            disabled={disabled}
            onChange={(event) =>
              onIsolatorIndex(event.target.value === '' ? undefined : Number(event.target.value))
            }
          >
            <option value="">Choose an Integrated or blank DC isolator…</option>
            {view.choices.map((index) => (
              <option key={index} value={index}>
                DC isolator {index + 1}
              </option>
            ))}
          </select>
        </label>
      )}
      {view.showPicker && view.choices.length === 0 && (
        <p>Inverter ratings are only offered for a DC isolator set to Integrated or left blank.</p>
      )}
      {view.offers.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Photo reading</th>
              <th>Apply</th>
            </tr>
          </thead>
          <tbody>
            {view.offers.map((offer) => {
              const selectable = !!offer.reading && !offer.blocked && !offer.needsConfirmation;
              const checked = overrides[offer.field] ?? offer.preTicked;
              return (
                <tr key={offer.field}>
                  <th scope="row">{offer.label}</th>
                  <td data-label="Photo reading">
                    {offer.needsConfirmation ? (
                      <span className="photo-check-uncertain">{offerReading(offer)}</span>
                    ) : (
                      offerReading(offer)
                    )}
                  </td>
                  <td data-label="Apply">
                    {selectable ? (
                      <input
                        aria-label={`Apply ${offer.label}`}
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(event) => onToggle(offer.field, event.target.checked)}
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
